/**
 * Générateur de textes alternatifs SEO-optimisés et multilingues
 * Respecte les bonnes pratiques SEO:
 * - Maximum 125 caractères
 * - Descriptions contextuelles avec mots-clés
 * - Pas de mots inutiles comme "image de" ou "logo de"
 * - Multilingue (FR/EN/ES)
 */

export type ImageType = 
  | 'server-logo'
  | 'server-banner'
  | 'game-logo'
  | 'game-backdrop'
  | 'partner-logo'
  | 'user-avatar'
  | 'arcade-spot'
  | 'competitive-event';

interface AltTextOptions {
  type: ImageType;
  name?: string;
  gameName?: string;
  context?: string;
  language?: 'fr' | 'en' | 'es';
}

const altTextTemplates = {
  'server-logo': {
    fr: (name: string, gameName?: string) => 
      gameName 
        ? `${name} - serveur ${gameName} privé classé` 
        : `${name} - serveur gaming privé`,
    en: (name: string, gameName?: string) => 
      gameName 
        ? `${name} - top ${gameName} private server` 
        : `${name} - private gaming server`,
    es: (name: string, gameName?: string) => 
      gameName 
        ? `${name} - servidor privado ${gameName} clasificado` 
        : `${name} - servidor gaming privado`,
  },
  'server-banner': {
    fr: (name: string, gameName?: string) => 
      gameName 
        ? `Bannière ${name} - serveur ${gameName} multijoueur` 
        : `Bannière serveur ${name}`,
    en: (name: string, gameName?: string) => 
      gameName 
        ? `${name} banner - ${gameName} multiplayer server` 
        : `${name} server banner`,
    es: (name: string, gameName?: string) => 
      gameName 
        ? `Banner ${name} - servidor ${gameName} multijugador` 
        : `Banner servidor ${name}`,
  },
  'game-logo': {
    fr: (name: string) => `${name} - serveurs privés classement`,
    en: (name: string) => `${name} - private servers ranking`,
    es: (name: string) => `${name} - clasificación servidores privados`,
  },
  'game-backdrop': {
    fr: (name: string) => `Fond ${name} - meilleurs serveurs privés`,
    en: (name: string) => `${name} background - best private servers`,
    es: (name: string) => `Fondo ${name} - mejores servidores privados`,
  },
  'partner-logo': {
    fr: (name: string) => `${name} - partenaire serveurs gaming`,
    en: (name: string) => `${name} - gaming servers partner`,
    es: (name: string) => `${name} - socio servidores gaming`,
  },
  'user-avatar': {
    fr: (name: string) => `Avatar joueur ${name}`,
    en: (name: string) => `Player ${name} avatar`,
    es: (name: string) => `Avatar jugador ${name}`,
  },
  'arcade-spot': {
    fr: (name: string, context?: string) => 
      context 
        ? `Publicité ${context} - espace arcade` 
        : `Publicité espace arcade ${name}`,
    en: (name: string, context?: string) => 
      context 
        ? `${context} advertisement - arcade space` 
        : `Arcade space ${name} advertisement`,
    es: (name: string, context?: string) => 
      context 
        ? `Publicidad ${context} - espacio arcade` 
        : `Publicidad espacio arcade ${name}`,
  },
  'competitive-event': {
    fr: (name: string) => `${name} - événement compétitif serveurs`,
    en: (name: string) => `${name} - competitive server event`,
    es: (name: string) => `${name} - evento competitivo servidores`,
  },
};

/**
 * Génère un texte alternatif SEO-optimisé et multilingue
 * @param options Configuration pour générer l'alt text
 * @returns Texte alternatif optimisé (max 125 caractères)
 */
export function generateSEOAltText(options: AltTextOptions): string {
  const { type, name = '', gameName, context, language = 'fr' } = options;
  
  if (!name) return '';
  
  const template = altTextTemplates[type]?.[language];
  if (!template) return name;
  
  let altText = '';
  
  // Générer le texte selon le type et les paramètres
  if (type === 'arcade-spot') {
    altText = template(name, context);
  } else if (['server-logo', 'server-banner'].includes(type)) {
    altText = template(name, gameName);
  } else {
    altText = template(name);
  }
  
  // Limiter à 125 caractères (bonne pratique SEO)
  if (altText.length > 125) {
    altText = altText.substring(0, 122) + '...';
  }
  
  return altText;
}

/**
 * Hook-friendly: génère un alt text en utilisant le type d'image et les données
 */
export function useGenerateSEOAltText() {
  return generateSEOAltText;
}
